import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Purchase } from '@/types/models';
import { correctPurchaseInvoiceRpc } from '@/services/purchases';
import { fetchSuppliers } from '@/services/suppliers';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { AlertTriangle, X, Edit3, Loader2, Laptop, Package, Lock, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

interface PurchaseCorrectionModalProps {
  purchase: Purchase;
  isOpen: boolean;
  onClose: () => void;
}

export const PurchaseCorrectionModal: React.FC<PurchaseCorrectionModalProps> = ({
  purchase,
  isOpen,
  onClose,
}) => {
  const { role } = useAuth();
  const isOwner = role === 'owner';
  const queryClient = useQueryClient();

  const [reason, setReason] = useState('');
  const [supplierId, setSupplierId] = useState(purchase.supplier_id ?? '');
  const [supplierInvoiceNumber, setSupplierInvoiceNumber] = useState(
    purchase.supplier_invoice_number ?? ''
  );
  const [purchaseDate, setPurchaseDate] = useState(
    purchase.purchase_date ? purchase.purchase_date.split('T')[0] : ''
  );
  const [notes] = useState(purchase.notes ?? '');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Editable Laptop Items State
  const initialLaptopCorrections = (purchase.items || [])
    .filter((i) => i.item_type === 'laptop' && i.laptop)
    .map((i) => ({
      laptop_id: i.laptop_id!,
      stock_id: i.laptop?.stock_id || '',
      brand: i.laptop?.brand || '',
      model: i.laptop?.model || '',
      serial_number: i.laptop?.serial_number || '',
      service_tag: i.laptop?.service_tag || '',
      barcode: i.laptop?.barcode || '',
      cpu: i.laptop?.cpu || '',
      ram: i.laptop?.ram || '',
      storage: i.laptop?.storage || '',
      gpu: i.laptop?.gpu || '',
      screen_size: i.laptop?.screen_size || '',
      touchscreen: i.laptop?.touchscreen ?? null,
      colour: i.laptop?.colour || '',
      condition: i.laptop?.condition || 'good',
      grade: i.laptop?.grade || '',
      battery_health: i.laptop?.battery_health || 100,
      charger_included: i.laptop?.charger_included ?? false,
      charger_notes: i.laptop?.charger_notes || '',
      warranty_months: i.laptop?.warranty_months || 0,
      purchase_price: Number(i.laptop?.purchase_price ?? i.unit_cost) || 0,
      allocated_cost: Number(i.allocated_cost) || 0,
      asking_price: i.laptop?.asking_price ?? null,
      minimum_price: i.laptop?.minimum_price ?? null,
      notes: i.laptop?.notes || '',
      status: i.laptop?.status || 'in_stock',
      isLocked: (i.laptop?.status !== 'in_stock' || !!i.laptop?.sold_to_customer_id),
    }));

  const [laptopCorrections, setLaptopCorrections] = useState(initialLaptopCorrections);

  // Editable Accessory Items State
  const initialAccessoryCorrections = (purchase.items || [])
    .filter((i) => i.item_type === 'accessory' && i.accessory)
    .map((i) => ({
      purchase_item_id: i.id,
      accessory_name: i.accessory?.name || i.description,
      sku: i.accessory?.sku || '',
      current_stock: i.accessory?.quantity ?? 0,
      original_qty: i.quantity,
      quantity: i.quantity,
      unit_cost: Number(i.unit_cost) || 0,
    }));

  const [accessoryCorrections, setAccessoryCorrections] = useState(initialAccessoryCorrections);
  const [discount, setDiscount] = useState<number>(purchase.discount || 0);

  const oldDiscount = purchase.discount || 0;
  const newDiscount = Math.max(0, Number(discount) || 0);
  const currentLaptopsSubtotal = laptopCorrections.reduce((acc, l) => acc + (Number(l.purchase_price) || 0), 0);
  const currentAccSubtotal = accessoryCorrections.reduce((acc, a) => acc + (Number(a.quantity) || 0) * (Number(a.unit_cost) || 0), 0);
  const currentSubtotal = currentLaptopsSubtotal + currentAccSubtotal;
  const oldTotal = purchase.total;
  const newTotal = Math.max(0, currentSubtotal + (purchase.shipping_cost || 0) + (purchase.other_cost || 0) - newDiscount);
  const totalDifference = newTotal - oldTotal;

  const { data: suppliersData, isLoading: loadingSuppliers } = useQuery({
    queryKey: ['suppliers', 'select-list'],
    queryFn: () => fetchSuppliers({ page: 0 }),
    enabled: isOpen,
  });

  const suppliers = suppliersData?.data ?? [];

  const updateLaptopField = (index: number, field: string, value: any) => {
    setLaptopCorrections((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const updateAccessoryField = (index: number, field: string, value: any) => {
    setAccessoryCorrections((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      if (!reason.trim()) throw new Error('A correction reason is required.');
      if (!isOwner) throw new Error('Only the owner can perform purchase and stock corrections.');

      return correctPurchaseInvoiceRpc({
        purchase_id: purchase.id,
        reason: reason.trim(),
        supplier_id: supplierId || null,
        supplier_invoice_number: supplierInvoiceNumber.trim() || null,
        purchase_date: purchaseDate || null,
        notes: notes.trim() || null,
        laptop_corrections: laptopCorrections.map((l) => ({
          laptop_id: l.laptop_id,
          brand: l.brand,
          model: l.model,
          serial_number: l.serial_number || null,
          service_tag: l.service_tag || null,
          barcode: l.barcode || null,
          cpu: l.cpu || null,
          ram: l.ram || null,
          storage: l.storage || null,
          gpu: l.gpu || null,
          screen_size: l.screen_size || null,
          touchscreen: l.touchscreen,
          colour: l.colour || null,
          condition: l.condition,
          grade: l.grade || null,
          battery_health: l.battery_health,
          charger_included: l.charger_included,
          charger_notes: l.charger_notes || null,
          warranty_months: l.warranty_months,
          purchase_price: l.purchase_price,
          allocated_cost: l.allocated_cost,
          asking_price: l.asking_price,
          minimum_price: l.minimum_price,
          notes: l.notes || null,
        })),
        accessory_corrections: accessoryCorrections.map((a) => ({
          purchase_item_id: a.purchase_item_id,
          quantity: a.quantity,
          unit_cost: a.unit_cost,
        })),
        discount: newDiscount,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.purchases.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.purchases.detail(purchase.id) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.laptops.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessories.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.reports.all });
      onClose();
    },
    onError: (err: unknown) => {
      setSubmitError(parseDatabaseError(err));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    mutate();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-brand-border">
          <div className="flex items-center space-x-2">
            <Edit3 className="w-5 h-5 text-amber-600" />
            <h2 className="font-bold text-brand-black text-sm">
              Safe Purchase & Stock Correction — {purchase.purchase_number}
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-brand-muted hover:text-brand-black transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="overflow-y-auto flex-1 px-5 py-4 space-y-4 text-xs">
            {/* Warning / Audit Notice */}
            <div className="flex items-start space-x-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-900">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600" />
              <div>
                <p className="font-bold">Owner-Only Purchase & Stock Correction</p>
                <p className="mt-0.5 text-amber-800">
                  You can correct purchase metadata, laptop specifications, and accessory quantities/costs.
                  Items with downstream sales or accounting transactions are locked from financial cost modification.
                </p>
              </div>
            </div>

            {/* Mandatory Reason */}
            <div>
              <label className="block font-bold text-brand-black mb-1">
                Correction Reason <span className="text-brand-red">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                placeholder="e.g. Corrected RAM specification on laptop, updated supplier invoice number..."
                className="w-full border border-brand-border rounded-md px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-brand-green resize-none"
                required
              />
            </div>

            {/* Purchase Header Metadata */}
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 p-3 bg-brand-bg rounded-lg border border-brand-border">
              <div>
                <label className="block font-bold text-brand-black mb-1">Supplier</label>
                {loadingSuppliers ? (
                  <Loader2 className="w-4 h-4 animate-spin text-brand-muted" />
                ) : (
                  <select
                    value={supplierId}
                    onChange={(e) => setSupplierId(e.target.value)}
                    className="w-full border border-brand-border rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-green"
                  >
                    <option value="">-- Select Supplier --</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block font-bold text-brand-black mb-1">Supplier Invoice #</label>
                <input
                  type="text"
                  value={supplierInvoiceNumber}
                  onChange={(e) => setSupplierInvoiceNumber(e.target.value)}
                  className="w-full border border-brand-border rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-green"
                />
              </div>

              <div>
                <label className="block font-bold text-brand-black mb-1">Purchase Date</label>
                <input
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  className="w-full border border-brand-border rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-green"
                />
              </div>

              <div>
                <label className="block font-bold text-brand-black mb-1">Purchase Discount (PKR)</label>
                <input
                  type="number"
                  min={0}
                  value={discount}
                  onChange={(e) => setDiscount(Math.max(0, Number(e.target.value) || 0))}
                  className="w-full border border-brand-border rounded-md px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-brand-green"
                />
              </div>
            </div>

            {/* Discount & Total Correction Preview */}
            <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-lg space-y-2">
              <h4 className="font-bold text-blue-950 uppercase tracking-wider text-[11px]">
                Financial Correction Preview
              </h4>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-[11px]">
                <div>
                  <span className="text-brand-muted block">Old Discount</span>
                  <span className="font-bold text-brand-black">Rs. {oldDiscount.toLocaleString('en-PK')}</span>
                </div>
                <div>
                  <span className="text-brand-muted block">New Discount</span>
                  <span className="font-bold text-amber-700">Rs. {newDiscount.toLocaleString('en-PK')}</span>
                </div>
                <div>
                  <span className="text-brand-muted block">Old Total</span>
                  <span className="font-bold text-brand-black">Rs. {oldTotal.toLocaleString('en-PK')}</span>
                </div>
                <div>
                  <span className="text-brand-muted block">New Total</span>
                  <span className="font-extrabold text-brand-green">Rs. {newTotal.toLocaleString('en-PK')}</span>
                </div>
                <div>
                  <span className="text-brand-muted block">Difference</span>
                  <span className={`font-bold ${totalDifference < 0 ? 'text-amber-700' : totalDifference > 0 ? 'text-brand-red' : 'text-brand-muted'}`}>
                    {totalDifference >= 0 ? '+' : ''}Rs. {totalDifference.toLocaleString('en-PK')}
                  </span>
                </div>
              </div>
              <p className="text-[10px] text-blue-900 pt-1 border-t border-blue-200">
                <strong>Inventory & COGS Impact:</strong> In-stock laptops will automatically adjust cost basis proportionally by the discount difference. Sold laptops will update historical sale base cost and COGS while preserving retail price and configuration adjustments.
              </p>
            </div>

            {/* Serialized Laptops Section */}
            {laptopCorrections.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center space-x-2 font-bold text-brand-black border-b border-brand-border pb-1">
                  <Laptop className="w-4 h-4 text-brand-green" />
                  <span>Purchased Laptops ({laptopCorrections.length})</span>
                </div>

                {laptopCorrections.map((lap, idx) => (
                  <div
                    key={lap.laptop_id}
                    className={`p-3 rounded-lg border space-y-2 ${
                      lap.isLocked ? 'bg-gray-50 border-gray-200' : 'bg-white border-brand-border'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <span className="font-bold text-brand-black">{lap.stock_id}</span>
                        <span className="text-brand-muted">({lap.brand} {lap.model})</span>
                      </div>
                      <div>
                        {lap.isLocked ? (
                          <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-amber-100 text-amber-900 text-[10px] font-bold rounded-full">
                            <Lock className="w-3 h-3" />
                            <span>Locked: Downstream Activity ({lap.status})</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center space-x-1 px-2 py-0.5 bg-emerald-100 text-emerald-900 text-[10px] font-bold rounded-full">
                            <CheckCircle2 className="w-3 h-3" />
                            <span>Safe Correction</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div>
                        <label className="text-[10px] text-brand-muted">Brand</label>
                        <input
                          type="text"
                          value={lap.brand}
                          onChange={(e) => updateLaptopField(idx, 'brand', e.target.value)}
                          className="w-full border border-brand-border rounded px-2 py-1"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-brand-muted">Model</label>
                        <input
                          type="text"
                          value={lap.model}
                          onChange={(e) => updateLaptopField(idx, 'model', e.target.value)}
                          className="w-full border border-brand-border rounded px-2 py-1"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-brand-muted">Serial Number</label>
                        <input
                          type="text"
                          value={lap.serial_number}
                          onChange={(e) => updateLaptopField(idx, 'serial_number', e.target.value)}
                          className="w-full border border-brand-border rounded px-2 py-1 font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-brand-muted">Service Tag</label>
                        <input
                          type="text"
                          value={lap.service_tag}
                          onChange={(e) => updateLaptopField(idx, 'service_tag', e.target.value)}
                          className="w-full border border-brand-border rounded px-2 py-1 font-mono"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-brand-muted">CPU</label>
                        <input
                          type="text"
                          value={lap.cpu}
                          onChange={(e) => updateLaptopField(idx, 'cpu', e.target.value)}
                          className="w-full border border-brand-border rounded px-2 py-1"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-brand-muted">RAM</label>
                        <input
                          type="text"
                          value={lap.ram}
                          onChange={(e) => updateLaptopField(idx, 'ram', e.target.value)}
                          className="w-full border border-brand-border rounded px-2 py-1"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-brand-muted">Storage</label>
                        <input
                          type="text"
                          value={lap.storage}
                          onChange={(e) => updateLaptopField(idx, 'storage', e.target.value)}
                          className="w-full border border-brand-border rounded px-2 py-1"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] text-brand-muted">Touchscreen</label>
                        <select
                          value={lap.touchscreen === true ? 'true' : lap.touchscreen === false ? 'false' : ''}
                          onChange={(e) => updateLaptopField(idx, 'touchscreen', e.target.value === 'true' ? true : e.target.value === 'false' ? false : null)}
                          className="w-full border border-brand-border rounded px-2 py-1 text-xs"
                        >
                          <option value="">Unknown / Not Set</option>
                          <option value="true">Touchscreen</option>
                          <option value="false">Non-Touch</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-brand-muted">Purchase Cost (Rs.)</label>
                        <input
                          type="number"
                          value={lap.purchase_price}
                          disabled={lap.isLocked}
                          onChange={(e) => updateLaptopField(idx, 'purchase_price', Number(e.target.value))}
                          className={`w-full border rounded px-2 py-1 font-bold ${
                            lap.isLocked ? 'bg-gray-100 text-gray-500 border-gray-300' : 'border-brand-border text-brand-black'
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Accessories Section */}
            {accessoryCorrections.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center space-x-2 font-bold text-brand-black border-b border-brand-border pb-1">
                  <Package className="w-4 h-4 text-brand-green" />
                  <span>Purchased Accessories ({accessoryCorrections.length})</span>
                </div>

                {accessoryCorrections.map((acc, idx) => (
                  <div key={acc.purchase_item_id} className="p-3 bg-white rounded-lg border border-brand-border space-y-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-bold text-brand-black">{acc.accessory_name}</span>
                        <span className="text-brand-muted ml-2">(SKU: {acc.sku})</span>
                      </div>
                      <span className="text-[11px] text-brand-muted">
                        Current In Stock: <strong>{acc.current_stock}</strong> (Orig. Purchased: {acc.original_qty})
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[10px] font-bold text-brand-black">Corrected Quantity</label>
                        <input
                          type="number"
                          min={1}
                          value={acc.quantity}
                          onChange={(e) => updateAccessoryField(idx, 'quantity', Number(e.target.value))}
                          className="w-full border border-brand-border rounded px-3 py-1.5 font-bold"
                        />
                      </div>
                      <div>
                        <label className="text-[10px] font-bold text-brand-black">Corrected Unit Cost (Rs.)</label>
                        <input
                          type="number"
                          min={0}
                          value={acc.unit_cost}
                          onChange={(e) => updateAccessoryField(idx, 'unit_cost', Number(e.target.value))}
                          className="w-full border border-brand-border rounded px-3 py-1.5 font-bold"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {submitError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 font-semibold">
                {submitError}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 px-5 py-4 border-t border-brand-border bg-brand-bg">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending || !reason.trim() || !isOwner}
              className="inline-flex items-center space-x-2 px-5 py-2 bg-amber-600 text-white text-sm font-bold rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              <span>{isPending ? 'Saving Correction…' : 'Save Safe Correction'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

